import { unionBy } from 'lodash';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { countOfferProposalPrice } from '../../../../shared/countOfferProposalPrice';
import {
  AgGridColumnView,
  OfferItem,
  TableType,
} from '../../../../shared/types';
import {
  ContractType,
  DatabaseOfferItem,
  DatabaseOfferWithItems,
} from '../../../../shared/types/offers';
import AgGrid from '../../components/PricingTable/AgGrid';
import { useColumns } from '../../hooks/useColumns';
import { useItems } from '../../hooks/useItems';
import NotificationProvider from '../../providers/Notification/NotificationProvider';
import {
  getSessionStorageArray,
  removeFromSessionStorageArray,
  StorageKeys,
} from '../../stores/sessionStorage';
import { getUpdatedOfferItems, NotificationText } from './helpers';

import OfferStepActionContainer from './OfferStepActionContainer';
import { OfferStepTitle } from './OfferStepTitle';
import { useUpdateOfferItem } from '../../hooks/useUpdateOfferItem';
import { useUpdateOfferItems } from '../../hooks/useUpdateOfferItems';
import { useDeleteOfferItems } from '../../hooks/useDeleteOfferItems';
import { WarningAmber } from '@mui/icons-material';
import { sortByItemType } from '../../../../shared/sortByItemType';
import { updateItemGroup } from '../../services/itemGroups';
import { countDaysInDateRange } from '../../../../shared/countDaysInDateRange';
import { calculateDayPriceToSinglePayment } from '../../helpers/calculateDayPriceToSinglePayment';
import useRamiturvaNames from '../../hooks/useRamiturvaNames';
import { isRerent } from '../../../../shared/helpers/isRerent';

type OfferItemDetailsProps = {
  offerSheet: DatabaseOfferWithItems;
  renderStepActions: () => ReactNode;
};

export const OfferItemDetails = ({
  offerSheet,
  renderStepActions = () => null,
}: OfferItemDetailsProps) => {
  const { id } = offerSheet;
  const { setNotification } = useContext(NotificationProvider);

  const offerSheetItemsRef = useRef<OfferItem[]>([]);
  const offerSheetItemsDatabaseRef = useRef<DatabaseOfferItem[]>([]);
  const { data: ramiturvaNames = {} } = useRamiturvaNames();
  const { data: offerSheetItems = [], isFetching: isLoadingItems } =
    useItems({
      includeHidden: true,
      offerSheetItemsOnly: true,
      offerSheet,
      ramiturvaNames,
    });

  offerSheetItemsRef.current = offerSheetItems;
  offerSheetItemsRef.current.sort(sortByItemType).reverse();
  offerSheetItemsDatabaseRef.current = offerSheet.items;

  // delete offer sheet item(s) mutation
  const { mutate: deleteOfferItems } = useDeleteOfferItems({
    offerSheetId: id,
  });
  // update offer sheet -ITEM- mutation
  const { mutate: updateOfferItem } = useUpdateOfferItem();
  // update offer sheet -ITEMS- mutation
  const { mutate: updateOfferItems } = useUpdateOfferItems();

  const handleLeaseChange = (newRow: OfferItem) => {
    const { leasePeriodStart, leasePeriodEnd } = newRow;
    const updatedItem = offerSheetItemsDatabaseRef.current.find(
      (item) => item.id === newRow.offerItemId,
    );

    const proposalPrices = countOfferProposalPrice({
      item: newRow,
      priceCategory: offerSheet.priceCategory,
      industry: offerSheet.industry,
      offerLeasePeriodStart: new Date(leasePeriodStart),
      offerLeasePeriodEnd: new Date(leasePeriodEnd),
      contractType: offerSheet.contractType,
    });

    if (
      leasePeriodStart !== updatedItem?.leasePeriodStart ||
      leasePeriodEnd !== updatedItem?.leasePeriodEnd
    ) {
      const hasSPUserInput = () => {
        if (
          updatedItem &&
          updatedItem.proposalDayPrice &&
          updatedItem.singlePaymentPrice
        ) {
          const previousProposalPriceSP =
            updatedItem.proposalDayPrice *
            countDaysInDateRange(
              new Date(updatedItem.leasePeriodStart),
              new Date(updatedItem.leasePeriodEnd),
            );
          return (
            Math.round(previousProposalPriceSP) !==
            Math.round(updatedItem.singlePaymentPrice)
          );
        }
        return false;
      };
      /**
       * If contract type is single payment, then single payment value stays the same
       * and day price is calculated according to changed dates
       */
      const calculateNewDayPrice = () => {
        if (
          offerSheet.contractType === ContractType.SinglePayment &&
          updatedItem?.singlePaymentPrice &&
          hasSPUserInput()
        ) {
          return calculateDayPriceToSinglePayment(
            updatedItem.singlePaymentPrice,
            new Date(leasePeriodStart),
            new Date(leasePeriodEnd),
          );
        }

        return updatedItem?.proposalDayPrice === updatedItem?.dayPrice
          ? proposalPrices.proposalDayPrice
          : updatedItem?.dayPrice;
      };
      handleUpdateRowValue({
        ...proposalPrices,
        dayPrice: calculateNewDayPrice(),
        monthPrice:
          updatedItem?.proposalMonthPrice === updatedItem?.monthPrice
            ? proposalPrices.proposalMonthPrice
            : updatedItem?.monthPrice,
        proposalDayPrice: proposalPrices.proposalDayPrice,
        proposalMonthPrice: proposalPrices.proposalMonthPrice,
        singlePaymentPrice: hasSPUserInput()
          ? updatedItem?.singlePaymentPrice
          : proposalPrices.singlePaymentPrice,
      });
    } else {
      handleUpdateRowValue(newRow);
    }
  };

  const handleUpdateRowValue = ({
    comment,
    dayPrice,
    leasePeriodEnd,
    leasePeriodStart,
    monthPrice,
    offerItemId,
    pricingBasis,
    proposalDayPrice,
    proposalMonthPrice,
    quantity,
    unit,
    includeRamiturva,
    ramiturvaDayPrice,
    ramiturvaMonthPrice,
    ramiturvaProposalDayPrice,
    ramiturvaProposalMonthPrice,
    singlePaymentPrice,
  }: OfferItem) => {
    console.log('unit', unit);
    updateOfferItem({
      id: String(offerItemId),
      item: {
        comment,
        dayPrice,
        leasePeriodEnd,
        leasePeriodStart,
        monthPrice,
        pricingBasis,
        proposalDayPrice,
        proposalMonthPrice,
        quantity,
        unit,
        includeRamiturva,
        ramiturvaDayPrice,
        ramiturvaMonthPrice,
        ramiturvaProposalDayPrice,
        ramiturvaProposalMonthPrice,
        singlePaymentPrice,
      },
    });
  };

  const handleMultipleRowValueUpdates = (
    updatedRows: OfferItem[],
    includeNonRentalItems = false,
  ) => {
    const updatedItems = unionBy(
      updatedRows,
      offerSheetItems,
      'offerItemId',
    );
    const updatedItemIds = updatedRows.map(({ id }) => id);
    const updatedDatabaseOfferItems = offerSheet.items.filter(
      (item) => {
        if (includeNonRentalItems) {
          return true;
        }
        const { itemId } = item;
        return (
          (itemId && updatedItemIds.includes(itemId)) ||
          isRerent(item)
        );
      },
    );
    updateOfferItems({
      id: String(id),
      items: getUpdatedOfferItems(
        updatedDatabaseOfferItems,
        updatedItems,
      ),
    });
    setNotification({
      type: 'SNACKBAR',
      duration: 3000,
      severity: 'success',
      message: 'Tuotteiden arvot päivitetty!',
    });
  };

  const handleCopyRow = (newRowCopy: OfferItem) => {
    const existingItem = offerSheetItemsDatabaseRef.current.find(
      (item) => item.id === newRowCopy.offerItemId,
    );

    if (existingItem) {
      const { id, ...itemWithoutId } = existingItem;
      const updatedOfferItems = [
        ...offerSheetItemsDatabaseRef.current,
        { ...itemWithoutId },
      ];
      updateOfferItems({
        id: String(itemWithoutId.offerSheetId),
        items: updatedOfferItems,
      });
    }
  };

  const handleRemoveRow = (removedRow: OfferItem) => {
    const removedId = removedRow.offerItemId;
    if (!removedId) return;
    const length = offerSheetItemsDatabaseRef.current.reduce(
      (count, item) =>
        item.itemId === removedRow.id ? count + 1 : count,
      0,
    );
    const name = removedRow.name || removedRow.pimDisplayName;

    // Show a modal if the last item of itemId X is being removed
    if (length === 1) {
      setNotification({
        type: 'MODAL',
        severity: 'info',
        message: `Olet poistamassa viimeistä tuotetta: ${removedRow.catClass} - ${name}`,
        title: 'Haluatko jatkaa?',
        onAccept: () => {
          deleteOfferItems({
            id: [removedId],
          });
          setNotification({
            type: 'SNACKBAR',
            severity: 'success',
            message: 'Tuote poistettu onnistuneesti',
            duration: 3000,
          });
        },
      });
    } else {
      deleteOfferItems({
        id: [removedId],
      });
      setNotification({
        type: 'SNACKBAR',
        severity: 'success',
        message: 'Tuote poistettu onnistuneesti',
        duration: 3000,
      });
    }
  };

  const { columnDefs, gridColumnView } = useColumns(
    AgGridColumnView.OFFER_ITEMDETAILS,
    {
      handleUpdateRowValue,
      handleRemoveRow,
      handleCopyRow,
      handleLeaseChange,
      language: offerSheet.language,
      includeRamiturvaOffer: offerSheet.includeRamiturva,
      contractType: offerSheet.contractType,
    },
  );

  // If user changes leasePeriod from offerDetails, show modal where user
  // can update the values to match the leasePeriod
  if (
    getSessionStorageArray(
      StorageKeys.ShowLeaseDateChangedDialog,
    ).includes(offerSheet.id)
  ) {
    removeFromSessionStorageArray(
      StorageKeys.ShowLeaseDateChangedDialog,
      offerSheet.id,
    );
    if (
      offerSheetItemsRef.current.some(
        (item) =>
          String(item.leasePeriodStart) !==
            offerSheet.offerLeasePeriodStart ||
          String(item.leasePeriodEnd) !==
            offerSheet.offerLeasePeriodEnd,
      )
    ) {
      setNotification({
        type: 'MODAL',
        severity: 'info',
        message: `Tarjouksen vuokra-aika on muuttunut. Muutetaanko tuotteiden alkamis- ja päättymisaika vastaamaan tarjouksen vuokra-aikaa?`,
        cancelText: 'Älä vaihda',
        acceptText: 'Vaihda',
        title: 'Muutos vuokra-ajassa',
        onAccept: () => {
          const updatedRows = offerSheetItemsRef.current.map(
            (item) => {
              let updatedItem = {
                ...item,
                leasePeriodStart: new Date(
                  offerSheet.offerLeasePeriodStart,
                ),
                leasePeriodEnd: new Date(
                  offerSheet.offerLeasePeriodEnd,
                ),
              };

              if (item.itemGroup) {
                const updatedItemGroup = {
                  ...item.itemGroup,
                  leasePeriodStart: new Date(
                    offerSheet.offerLeasePeriodStart,
                  ),
                  leasePeriodEnd: new Date(
                    offerSheet.offerLeasePeriodEnd,
                  ),
                  totalPrice: item.itemGroup.totalPrice ?? 0,
                };

                updateItemGroup(updatedItemGroup);
                updatedItem = {
                  ...updatedItem,
                  itemGroup: updatedItemGroup,
                };
              }

              return updatedItem;
            },
          );
          handleMultipleRowValueUpdates(updatedRows);
        },
      });
    }
  }

  useEffect(() => {
    // If user changes language, update unit fields of every item
    if (
      getSessionStorageArray(StorageKeys.LanguageHasChanged).includes(
        offerSheet.id,
      )
    ) {
      removeFromSessionStorageArray(
        StorageKeys.LanguageHasChanged,
        offerSheet.id,
      );

      const updatedRows = offerSheetItemsRef.current.map((item) => {
        let updatedItem = {
          ...item,
          unit: offerSheet.language === 'en' ? 'pcs' : 'kpl',
        };

        return updatedItem;
      });
      handleMultipleRowValueUpdates(updatedRows, true);
    }
  }, [
    offerSheetItems.length,
    handleMultipleRowValueUpdates,
    offerSheet.id,
    offerSheet.language,
  ]);

  return (
    <>
      <OfferStepActionContainer>
        {renderStepActions()}
      </OfferStepActionContainer>
      <OfferStepTitle
        title={'Muokkaa tuotteiden tietoja'}
        offerSheet={offerSheet}
      />
      {!isLoadingItems && offerSheetItems.length === 0 && (
        <NotificationText>
          <WarningAmber />
          Tuotteita ei löytynyt!
        </NotificationText>
      )}
      {!isLoadingItems && offerSheetItems.length > 0 && (
        <AgGrid
          autoHeight
          fullWidthProductCell
          hideSideBar
          gridColumnView={gridColumnView}
          columnDefs={columnDefs}
          type={TableType.OfferItemDetails}
          rows={offerSheetItems}
          loading={false}
          externalFilters={[]}
          isSelectable={true}
          expandGroups={true}
        />
      )}
    </>
  );
};
